import type { NostrEvent } from "@nostrify/nostrify";
import type { BlossomServerMetadata } from "@/contexts/AppContext";

/** Normalize a Blossom server URL for deduplication (lowercase, strip trailing slashes). */
function normalizeUrl(url: string): string {
  return url.toLowerCase().replace(/\/+$/, "");
}

/** Parse a kind 10063 Blossom server list event into validated server URLs. */
export function parseBlossomServerList(event: NostrEvent): string[] {
  return event.tags
    .filter(([name]) => name === "server")
    .map(([, url]) => url)
    .filter((url) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    });
}

/**
 * App default Blossom servers used as a fallback when the user has no kind 10063
 * server list, and optionally merged with user servers via `useAppBlossomServers`.
 */
export const APP_BLOSSOM_SERVERS: BlossomServerMetadata = {
  servers: [
    "https://blossom.primal.net/",
    "https://blossom.nostr.build/",
    "https://blossom.ditto.pub/",
  ],
  updatedAt: 0,
};

/**
 * Get the effective Blossom server list based on user settings.
 *
 * - If `useAppBlossomServers` is true, merges app servers with user servers (deduped,
 *   app servers first per BUD-03's "most trusted first" ordering).
 * - If `useAppBlossomServers` is false, returns only the user's servers (deduped).
 */
export function getEffectiveBlossomServers(
  userMeta: BlossomServerMetadata,
  useAppBlossomServers: boolean,
): string[] {
  if (!useAppBlossomServers) {
    return deduplicateServers(userMeta.servers);
  }

  const seen = new Set<string>();
  const merged: string[] = [];

  for (const url of [...APP_BLOSSOM_SERVERS.servers, ...userMeta.servers]) {
    const normalized = normalizeUrl(url);
    if (!seen.has(normalized)) {
      seen.add(normalized);
      merged.push(url);
    }
  }

  return merged;
}

/** Deduplicate servers by normalized URL, preserving order. */
function deduplicateServers(servers: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const url of servers) {
    const normalized = normalizeUrl(url);
    if (!seen.has(normalized)) {
      seen.add(normalized);
      result.push(url);
    }
  }
  return result;
}
